import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import config from '@/config';
import api from '@/shared/api';
import { useEffect, useState, useCallback, useMemo } from 'react';
import Axios from 'axios';
import InfiniteScroll from 'react-infinite-scroller';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import { FormattedTime, useIntl, defineMessages } from 'react-intl';
import ToastrService from '@/shared/services/ToastrService';
import dateFormatService from '@/shared/services/DateFormatService';
import DataCacheService from '@/shared/services/DataCacheService';
import moment from 'moment';
import { formatListValue } from '@/shared/components/FormattedListValue';
import DocumentService from '@/shared/services/DocumentService';
import { DownloadIcon } from '@/shared/components/Icons';
import TaskStatusIcon from '@/modules/tasks/TaskStatusIcon';
const onlyUnique = (value, index, self) => self.indexOf(value) === index;
const pageSize = 50;
const adminUserId = '11111111-1111-1111-1111-111111111111';
const dateFormat = 'YYYY-MM-DDTHH:mm:ssZ';
const messages = defineMessages({
    ATaskWasLinked: {
        id: 'ATaskWasLinked',
        defaultMessage: 'A task was linked to this document',
    },
    ATaskWasUnlinked: {
        id: 'ATaskWasUnlinked',
        defaultMessage: 'A task was unlinked from this document',
    },
    DocumentAuditLogsNotAvailable: {
        id: 'DocumentAuditLogsNotAvailable',
        defaultMessage: 'For technical reasons, audit logs are not available for this document because it was uploaded before october 10th 2024. Sorry for the inconvenience.',
    },
    ThisTaskWasDeleted: {
        id: 'ThisTaskWasDeleted',
        defaultMessage: 'this task was deleted',
    },
    Tasks: {
        id: 'Tasks',
        defaultMessage: 'Tasks',
    },
    AuditLog: {
        id: 'AuditLog',
        defaultMessage: 'AuditLog',
    },
    DownloadThisVersionOfTheDocument: {
        id: 'DownloadThisVersionOfTheDocument',
        defaultMessage: 'Download this version of the document',
    },
    Download: {
        id: 'Download',
        defaultMessage: 'Download',
    },
    RecognitionStatus: {
        id: 'RecognitionStatus',
        defaultMessage: 'RecognitionStatus',
    },
    Tags: {
        id: 'Tags',
        defaultMessage: 'Tags',
    },
    Relations: {
        id: 'Relations',
        defaultMessage: 'Relations',
    },
    SigningFailedReason: {
        id: 'SigningFailedReason',
        defaultMessage: 'SigningFailedReason',
    },
    Yes: {
        id: 'Yes',
        defaultMessage: 'Yes',
    },
    No: {
        id: 'No',
        defaultMessage: 'No',
    },
    IsPublicOnPortal: {
        id: 'IsPublicOnPortal',
        defaultMessage: 'IsPublicOnPortal',
    },
    SigningRequestDate: {
        id: 'SigningRequestDate',
        defaultMessage: 'SigningRequestDate',
    },
    SigningDate: {
        id: 'SigningDate',
        defaultMessage: 'SigningDate',
    },
    DocumentStartDate: {
        id: 'DocumentStartDate',
        defaultMessage: 'DocumentStartDate',
    },
    DocumentEndDate: {
        id: 'DocumentEndDate',
        defaultMessage: 'DocumentEndDate',
    },
    DocumentDate: {
        id: 'DocumentDate',
        defaultMessage: 'DocumentDate',
    },
    SetEnum: {
        id: 'SetEnum',
        defaultMessage: '{property} was set to {value}',
    },
    ClearEnum: {
        id: 'ClearEnum',
        defaultMessage: '{property} was cleared. Old value: {value}',
    },
    SigningStatus: {
        id: 'SigningStatus',
        defaultMessage: 'SigningStatus',
    },
    DocumentType: {
        id: 'DocumentType',
        defaultMessage: 'DocumentType',
    },
    Filename: {
        id: 'Filename',
        defaultMessage: 'Filename',
    },
    ChangedField: {
        id: 'ChangedField',
        defaultMessage: '{property} was changed from {originalValue} to {currentValue}',
    },
    DocumentContentModified: {
        id: 'DocumentContentModified',
        defaultMessage: 'Document content was modified',
    },
    DocumentCheckedIn: {
        id: 'DocumentCheckedOn',
        defaultMessage: 'Document content was checked in by {name}',
    },
    DocumentCheckedOut: {
        id: 'DocumentCheckedOut',
        defaultMessage: 'Document content was checked out by {name}',
    },
    DocumentCheckoutDiscarded: {
        id: 'DocumentCheckoutDiscarded',
        defaultMessage: 'Document checkout was discarded by {name}',
    },
    DocumentCreated: {
        id: 'DocumentCreated',
        defaultMessage: 'Document was created',
    },
    NoAuditLinesFound: {
        id: 'NoAuditLinesFound',
        defaultMessage: 'No audit lines found',
    },
    GenericDocumentRecognition: {
        id: 'GenericDocumentRecognition',
        defaultMessage: 'Generic document recognition',
    },
    ReferenceYear: {
        id: 'ReferenceYear',
        defaultMessage: 'Reference year',
    },
});
export const DocumentAudits = ({ documentId, storageType, creationDate }) => {
    const intl = useIntl();
    const isDevelopment = config.environment.toLowerCase() === 'local' ||
        config.environment.toLowerCase() === 'development';
    const isAcceptance = config.environment.toLowerCase() === 'acceptance';
    const [auditLines, setAuditLines] = useState([]);
    const [auditLogsAvailable, setAuditLogsAvailable] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [relationIds, setRelationIds] = useState([]);
    const [taskIds, setTaskIds] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [usersDictionary, setUsersDictionary] = useState({});
    const [relationsDictionary, setRelationsDictionary] = useState({});
    const [tasksDictionary, setTasksDictionary] = useState({});
    const [parentRef, setParentRef] = useState(null);
    useEffect(() => {
        const auditLogsReleaseDate = isDevelopment
            ? moment('20-09-2024', 'DD-MM-YYYY')
            : isAcceptance
                ? moment('03-10-2024', 'DD-MM-YYYY')
                : moment('10-10-2024', 'DD-MM-YYYY');
        const internalAuditLogsAvailable = moment
            .utc(creationDate)
            .startOf('day')
            .isSameOrAfter(auditLogsReleaseDate.startOf('day'));
        // re-load audits when the document has changed
        setAuditLogsAvailable(internalAuditLogsAvailable);
        setLoading(false);
        setHasError(false);
        setAuditLines([]);
        if (internalAuditLogsAvailable) {
            setHasMore(true);
        }
    }, [documentId, isDevelopment, isAcceptance, creationDate]);
    useEffect(() => {
        const newUsersDictionary = DataCacheService.getAllUsers().reduce((prev, current) => (Object.assign(Object.assign({}, prev), { [current.id]: current.name })), {});
        setUsersDictionary(newUsersDictionary);
    }, []);
    useEffect(() => {
        if (relationIds.length === 0)
            return;
        const uri = `${config.appUrls.api}/api/Relation/Names`;
        const fetchRelations = () => {
            api.base.post(uri, relationIds).then(({ data }) => {
                const newRelationsDictionary = data.reduce((prev, current) => (Object.assign(Object.assign({}, prev), { [current.id]: `${current.name} ${current.firstName ? current.firstName : ''}` })), {});
                setRelationsDictionary(current => (Object.assign(Object.assign({}, (current || {})), newRelationsDictionary)));
            });
        };
        fetchRelations();
    }, [relationIds, intl]);
    useEffect(() => {
        if (taskIds.length === 0)
            return;
        const uri = `${config.appUrls.tasks}/api/plannedtasks/tasks`;
        const fetchTasks = () => {
            api.base.post(uri, taskIds).then(({ data }) => {
                const newTasksDictionary = data.reduce((prev, current) => (Object.assign(Object.assign({}, prev), { [current.id]: current })), {});
                setTasksDictionary(current => (Object.assign(Object.assign({}, (current || {})), newTasksDictionary)));
            });
        };
        fetchTasks();
    }, [taskIds, intl]);
    const loadNextPage = useCallback(page => {
        const cancelToken = Axios.CancelToken.source();
        const uri = `${config.appUrls.audit}/api/Documents/${documentId}?limit=${pageSize}&offset=${page * pageSize}`;
        setLoading(true);
        return api.base.get(uri, { cancelToken: cancelToken.token }).then(({ data }) => {
            const reducedData = data.map(logLine => (Object.assign(Object.assign({}, logLine), { id: logLine.dateTime, dateTime: new Date(logLine.dateTime), type: logLine.type.toLowerCase() })));
            if (data.length < pageSize)
                setHasMore(false);
            setAuditLines([...auditLines, ...reducedData]);
            const allRelationIds = reducedData
                .reduce((result, logLine) => {
                if (logLine.type.toLowerCase() === 'field' &&
                    logLine.property &&
                    logLine.property.toLowerCase() === 'relationids') {
                    const newSplitRelationIds = logLine.newValue
                        ? logLine.newValue.split(',').map(x => x.trim())
                        : [];
                    const oldSplitRelationIds = logLine.oldValue
                        ? logLine.oldValue.split(',').map(x => x.trim())
                        : [];
                    newSplitRelationIds.forEach(x => {
                        result.push(x);
                    });
                    oldSplitRelationIds.forEach(x => {
                        result.push(x);
                    });
                }
                return result;
            }, [])
                .filter(x => x !== null && typeof x !== 'undefined')
                .filter(onlyUnique);
            setRelationIds(allRelationIds);
            const allTaskIds = reducedData
                .reduce((result, logLine) => {
                if (logLine.type.toLowerCase() === 'field' &&
                    logLine.property &&
                    logLine.property.toLowerCase() === 'taskids') {
                    const newSplitTaskIds = logLine.newValue
                        ? logLine.newValue.split(',').map(x => x.trim())
                        : [];
                    const oldSplitTaskIds = logLine.oldValue
                        ? logLine.oldValue.split(',').map(x => x.trim())
                        : [];
                    newSplitTaskIds.forEach(x => {
                        result.push(x);
                    });
                    oldSplitTaskIds.forEach(x => {
                        result.push(x);
                    });
                }
                return result;
            }, [])
                .filter(x => x !== null && typeof x !== 'undefined')
                .filter(onlyUnique);
            setTaskIds(allTaskIds);
            setLoading(false);
            return reducedData;
        }, error => {
            setLoading(false);
            setHasError(true);
            ToastrService.generalError(intl);
            throw error;
        });
    }, [documentId, auditLines, intl]);
    const groupedLogs = useMemo(() => {
        if (!auditLines || auditLines.length === 0)
            return [];
        return Object.values(auditLines.reduce((groups, log) => {
            const groupKey = dateFormatService.formatDate(log.dateTime);
            if (!groups[groupKey])
                // eslint-disable-next-line no-param-reassign
                groups[groupKey] = {
                    key: groupKey,
                    label: moment(log.dateTime).format('LL'),
                    logs: [],
                };
            groups[groupKey].logs.push(log);
            return groups;
        }, {}));
    }, [auditLines]);
    const renderSource = (source, documentType) => {
        if (!source || source === 'Unknown' || source === 'Manual' || source === 0 || source === 1)
            return null;
        const sourceFormatted = () => {
            if (Number.isInteger(source)) {
                return formatListValue({ id: source, list: 'entitysourcetype', intl });
            }
            if (source === 'GenericDocumentRecognition' && documentType) {
                return `${intl.formatMessage(messages.GenericDocumentRecognition)} (${formatListValue({
                    id: documentType,
                    list: 'documenttype',
                    intl,
                })})`;
            }
            return source in messages ? intl.formatMessage(messages[source]) : source;
        };
        return (_jsxs("span", { children: [' ', "via ", _jsx("b", { children: sourceFormatted() })] }));
    };
    const parseDate = value => {
        try {
            const mom = moment(value, dateFormat, true);
            if (!mom.isValid())
                return null;
            return dateFormatService.formatDate(mom);
        }
        catch (_) {
            return null;
        }
    };
    const parseBoolean = value => {
        switch (value) {
            case true:
            case 'True':
            case 'true':
                return true;
            case false:
            case 'False':
            case 'false':
                return false;
            default:
                return null;
        }
    };
    const translatedFieldChange = (property, originalValue, currentValue) => intl.formatMessage(messages.ChangedField, {
        property,
        originalValue: originalValue || '/',
        currentValue: currentValue || '/',
        b: value => _jsx("b", Object.assign({ className: "dark" }, { children: value })),
        i: value => _jsx("i", Object.assign({ className: "dark" }, { children: value })),
    });
    const translatedEnumChange = (translatedEnumType, enumType, oldValue, newValue) => {
        if (!oldValue) {
            return intl.formatMessage(messages.SetEnum, {
                property: translatedEnumType,
                value: formatListValue({ id: newValue, list: enumType, intl }),
                b: value => _jsx("b", Object.assign({ className: "dark" }, { children: value })),
                i: value => _jsx("i", Object.assign({ className: "dark" }, { children: value })),
            });
        }
        if (!newValue) {
            console.log('cleared', oldValue);
            return intl.formatMessage(messages.ClearEnum, {
                property: translatedEnumType,
                value: formatListValue({ id: oldValue, list: enumType, intl }),
                b: value => _jsx("b", Object.assign({ className: "dark" }, { children: value })),
                i: value => _jsx("i", Object.assign({ className: "dark" }, { children: value })),
            });
        }
        return intl.formatMessage(messages.ChangedField, {
            property: translatedEnumType,
            originalValue: formatListValue({
                id: oldValue,
                list: enumType,
                intl,
            }),
            currentValue: formatListValue({
                id: newValue,
                list: enumType,
                intl,
            }),
            b: value => _jsx("b", Object.assign({ className: "dark" }, { children: value })),
            i: value => _jsx("i", Object.assign({ className: "dark" }, { children: value })),
        });
    };
    const renderEnumChange = (log, enumType) => {
        var _a, _b;
        if (enumType.toLowerCase() === 'documenttype') {
            return (_jsx("span", { children: translatedEnumChange(intl.formatMessage(messages.DocumentType), enumType, (_a = log.oldValue) === null || _a === void 0 ? void 0 : _a.Value, (_b = log.newValue) === null || _b === void 0 ? void 0 : _b.Value) }));
        }
        if (enumType.toLowerCase() === 'signingstatus') {
            return (_jsx("span", { children: translatedEnumChange(intl.formatMessage(messages.SigningStatus), enumType, log.oldValue, log.newValue) }));
        }
        if (enumType.toLowerCase() === 'recognitionstatus') {
            return (_jsx("span", { children: translatedEnumChange(intl.formatMessage(messages.RecognitionStatus), enumType, log.oldValue, log.newValue) }));
        }
        return _jsxs("span", { children: ["Unmapped enum change: ", log.property] });
    };
    const parseRelations = relations => relations
        ? relations
            .split(',')
            .map(x => x.trim())
            .map(x => {
            if (x in (relationsDictionary || {}))
                return relationsDictionary[x];
            return null;
        })
            .join(', ')
        : '/';
    const getTaskName = task => {
        const taskName = task.name
            ? task.name
            : formatListValue({
                id: task.templateId,
                list: 'tasktemplate',
                intl,
                fallback: task.name || task.templateName || task.templateId,
            });
        const taskDeadline = task.deadline ? dateFormatService.formatDate(task.deadline) : null;
        return taskDeadline ? (_jsxs("b", Object.assign({ className: "dark" }, { children: [taskName, " (", taskDeadline, ")"] }))) : (_jsx("b", Object.assign({ className: "dark" }, { children: taskName })));
    };
    const renderSingleTask = taskId => {
        if (taskId in (tasksDictionary || {})) {
            const task = tasksDictionary[taskId];
            return (_jsxs(_Fragment, { children: [_jsx(TaskStatusIcon, { status: task.status, isBlocked: task.isBlocked, inapplicable: task.inapplicable, rejected: task.rejected, small: true, className: "margin5R" }), getTaskName(task)] }));
        }
        return (_jsxs("span", Object.assign({ className: "italic" }, { children: [intl.formatMessage(messages.ThisTaskWasDeleted), ' ', _jsxs("span", Object.assign({ style: { color: 'white' } }, { children: ["(id: ", taskId, ")"] }))] })));
    };
    const renderTasksChange = log => {
        const newTasks = log.newValue ? log.newValue.split(',').map(x => x.trim()) : [];
        const oldTasks = log.oldValue ? log.oldValue.split(',').map(x => x.trim()) : [];
        const addedTasks = newTasks.filter(task => !oldTasks.includes(task));
        const deletedTasks = oldTasks.filter(task => !newTasks.includes(task));
        if (addedTasks.length > 0) {
            return (_jsxs("span", { children: [intl.formatMessage(messages.ATaskWasLinked), ": ", addedTasks.map(x => renderSingleTask(x))] }));
        }
        if (deletedTasks.length > 0) {
            return (_jsxs("span", { children: [intl.formatMessage(messages.ATaskWasUnlinked), ":", ' ', deletedTasks.map(x => renderSingleTask(x))] }));
        }
        return '/';
    };
    const renderFieldChange = (log, property) => {
        switch (property.toLowerCase()) {
            case 'filename':
                return translatedFieldChange(intl.formatMessage(messages.Filename), log.oldValue, log.newValue);
            case 'ispubliconportal':
                return translatedFieldChange(intl.formatMessage(messages.IsPublicOnPortal), parseBoolean(log.oldValue)
                    ? intl.formatMessage(messages.Yes)
                    : intl.formatMessage(messages.No), parseBoolean(log.newValue)
                    ? intl.formatMessage(messages.Yes)
                    : intl.formatMessage(messages.No));
            case 'documentdate':
                return translatedFieldChange(intl.formatMessage(messages.DocumentDate), parseDate(log.oldValue), parseDate(log.newValue));
            case 'relationids':
                return translatedFieldChange(intl.formatMessage(messages.Relations), parseRelations(log.oldValue), parseRelations(log.newValue));
            case 'taskids':
                return renderTasksChange(log);
            case 'tags':
                return translatedFieldChange(intl.formatMessage(messages.Tags), log.oldValue, log.newValue);
            case 'documentstartdate':
                return translatedFieldChange(intl.formatMessage(messages.DocumentStartDate), parseDate(log.oldValue), parseDate(log.newValue));
            case 'documentenddate':
                return translatedFieldChange(intl.formatMessage(messages.DocumentEndDate), parseDate(log.oldValue), parseDate(log.newValue));
            case 'signingdate':
                return translatedFieldChange(intl.formatMessage(messages.SigningDate), parseDate(log.oldValue), parseDate(log.newValue));
            case 'signingrequestdate':
                return translatedFieldChange(intl.formatMessage(messages.SigningRequestDate), parseDate(log.oldValue), parseDate(log.newValue));
            case 'signingstatus':
                return renderEnumChange(log, property);
            case 'recognitionstatus':
                return renderEnumChange(log, property);
            case 'signingfailedreason':
                return translatedFieldChange(intl.formatMessage(messages.SigningFailedReason), log.oldValue, log.newValue);
            case 'referenceyear':
                return translatedFieldChange(intl.formatMessage(messages.ReferenceYear), log.oldValue, log.newValue);
            default:
                return _jsxs("span", { children: ["Unmapped property: ", property] });
        }
    };
    const downloadVersion = (blobVersionId, filename, mimeType) => {
        if (storageType === 1)
            return null; // currently not able to retrieve/download specific version of a file located in sharepoint
        return (_jsx("a", Object.assign({ style: { verticalAlign: 'middle', marginLeft: '5px' }, title: intl.formatMessage(messages.DownloadThisVersionOfTheDocument), onClick: () => DocumentService.download(documentId, blobVersionId, filename, mimeType) }, { children: _jsx(DownloadIcon, {}) })));
    };
    const renderLogLine = log => {
        const { type, property } = log;
        switch (type.toLowerCase()) {
            case 'entitycreated':
                return (_jsxs("span", { children: [intl.formatMessage(messages.DocumentCreated), log.blobVersionId
                            ? downloadVersion(log.blobVersionId, log.fileName, log.mimeType)
                            : null] }));
            case 'documentcontentmodified':
                return (_jsxs("span", { children: [intl.formatMessage(messages.DocumentContentModified), log.blobVersionId
                            ? downloadVersion(log.blobVersionId, log.fileName, log.mimeType)
                            : null] }));
            case 'documentcheckedout':
                return (_jsx("span", { children: intl.formatMessage(messages.DocumentCheckedOut, {
                        name: DataCacheService.getUserName(log.userId),
                    }) }));
            case 'documentcheckedin':
                return (_jsxs("span", { children: [intl.formatMessage(messages.DocumentCheckedIn, {
                            name: DataCacheService.getUserName(log.userId),
                        }), log.blobVersionId
                            ? downloadVersion(log.blobVersionId, log.fileName, log.mimeType)
                            : null] }));
            case 'documentcheckoutdiscarded':
                return (_jsx("span", { children: intl.formatMessage(messages.DocumentCheckoutDiscarded, {
                        name: DataCacheService.getUserName(log.userId),
                    }) }));
            case 'field':
                return renderFieldChange(log, property);
            case 'enum':
                return renderEnumChange(log, property);
            default:
                return _jsxs("span", { children: ["Unmapped log type: ", type] });
        }
    };
    const renderLogs = logs => {
        if (!logs)
            return _jsx("div", {});
        return logs.map(logLine => (_jsxs("li", Object.assign({ className: "document-audit-container-item list-group-item" }, { children: [_jsx("div", Object.assign({ className: "log" }, { children: renderLogLine(logLine) })), _jsxs("div", Object.assign({ className: "info" }, { children: [_jsx(FormattedTime, { value: logLine.dateTime }), logLine.userId === adminUserId ? _jsx("span", Object.assign({ className: "name" }, { children: "AdminPulse" })) : null, usersDictionary[logLine.userId] && (_jsx("span", Object.assign({ className: "name" }, { children: usersDictionary[logLine.userId] }))), renderSource(logLine.source, logLine.documentType)] }))] }))));
    };
    const renderGroups = () => {
        if (!groupedLogs || groupedLogs.length === 0)
            return loading ? null : intl.formatMessage(messages.NoAuditLinesFound);
        return groupedLogs
            .filter(x => x !== null)
            .map(({ label, logs }) => (_jsxs("div", Object.assign({ className: "audit-group" }, { children: [_jsx("div", Object.assign({ className: "audit-group-label" }, { children: label })), _jsx("ul", Object.assign({ className: "audit-group-logs list-group" }, { children: renderLogs(logs) }))] }))));
    };
    return (_jsx("div", Object.assign({ className: "audits" }, { children: _jsx("div", Object.assign({ className: "document-audit-container" }, { children: _jsx("div", Object.assign({ className: "flex-col", ref: node => {
                    setParentRef(node);
                }, style: { height: '700px', overflow: 'auto' } }, { children: auditLogsAvailable ? (_jsx(InfiniteScroll, Object.assign({ pageStart: -1, loadMore: loadNextPage, hasMore: hasMore && !loading && !hasError, initialLoad: true, loader: _jsx(LoadingAnimation, {}), useWindow: false, getScrollParent: () => parentRef }, { children: renderGroups() }), documentId)) : (intl.formatMessage(messages.DocumentAuditLogsNotAvailable)) })) })) })));
};
